import type { ClienteApi, OpcionesPeticion } from '../cliente';
import { claveIdempotencia, consulta, negocio, type Pagina } from '../comun';
import type { components } from '../tipos/citas';
import { operacionesCitas } from './citas';
import { operacionesWebPublicaCliente } from './webPublicaCliente';

type Esquema<N extends keyof components['schemas']> = components['schemas'][N];

/** Pedir cita (9.9), «Mis citas» y el enlace de la cita (9.10), con los tipos de `citas.yaml` y `web.yaml`. */
export type { SitioPublico, WidgetPublico } from './webPublicaCliente';
export type CatalogoPublico = Esquema<'PublicCatalog'>;
export type ServicioPublico = Esquema<'PublicService'>;
export type ProfesionalPublico = Esquema<'PublicProfessional'>;
export type PreguntaCita = Esquema<'Question'>;
export type ServicioPedidoCliente = Esquema<'ServiceRequest'>;
export type RespuestaPedida = Esquema<'AnswerInput'>;
export type PresupuestoCliente = Esquema<'Quote'>;
export type ResultadoHuecosCliente = Esquema<'SlotSearchResult'>;
export type HuecoCliente = Esquema<'AppointmentSlot'>;
export type MotivoSinHueco = Esquema<'UnavailableReason'>;
export type PeticionCita = Esquema<'AppointmentBooking'>;
export type MiCita = Esquema<'MyAppointment'>;
export type MiCitaConPago = Esquema<'MyAppointmentWithPayment'>;
export type ListaMisCitas = Esquema<'MyAppointmentList'>;
export type CambioMiCita = Esquema<'MyAppointmentModification'>;
export type VistaPreviaMiCambio = Esquema<'ModificationPreview'>;
export type VistaPreviaMiCancelacion = Esquema<'CancellationPreview'>;
export type CancelacionMiCita = Esquema<'MyAppointmentCancellation'>;

const conClave = (clave: string, extra: Record<string, string> = {}): Record<string, string> => ({ 'X-Api-Key': clave, ...extra });
/** El cliente de la API con la clave pública del negocio (`X-Api-Key`) y sin el token de cliente. */
export function conClavePublica(api: ClienteApi, clave: string): ClienteApi {
    const con = (o: OpcionesPeticion = {}): OpcionesPeticion => ({ ...o, sinSesion: true, cabeceras: { ...o.cabeceras, 'X-Api-Key': clave } });
    return {
        pedir: (metodo, ruta, opciones) => api.pedir(metodo, ruta, con(opciones)),
        pedirConVersion: (metodo, ruta, opciones) => api.pedirConVersion(metodo, ruta, con(opciones)),
    };
}

const misCitas = (perfilId: string): `/v1/${string}` => `/v1/customer-account/profiles/${encodeURIComponent(perfilId)}/appointments`;
const enlaceCita = (negocioId: string, token: string): `/v1/${string}` => `/v1/public/businesses/${encodeURIComponent(negocioId)}/appointment-links/${encodeURIComponent(token)}`;

/**
 * Operaciones de Citas en las páginas del cliente.
 *
 * - Las de pedir cita (9.9) llevan la clave pública **del negocio** (`X-Api-Key`: la de su web, `PublicSite.api_key`, o la del
 *   widget) y, las de leer, sin el token de cliente (`sinSesion`); `bookAppointment` lleva además el token, el captcha y la
 *   `Idempotency-Key` (la misma al repetir el mismo intento).
 * - «Mis citas» (9.10) va con el token de cliente; el enlace del mensaje es anónimo (su token, sin sesión ni clave).
 * - Nunca se calcula dinero: precio, señal y devolución los da la API.
 */
export function operacionesCitasCliente(api: ClienteApi) {
    return {
        ...operacionesWebPublicaCliente(api),
        /** getAppointmentCatalog: servicios con su versión vigente, preguntas y profesionales elegibles. */
        getAppointmentCatalog: (negocioId: string, clave: string) => api.pedir<CatalogoPublico>('GET', `${negocio(negocioId)}/appointment-catalog`, { sinSesion: true, cabeceras: conClave(clave) }),
        /**
         * Las operaciones de Citas del panel (`ops/citas.ts`: `quoteAppointment`, `searchAppointmentSlots`…) con la clave
         * pública del negocio y sin el token de cliente: la misma operación, otra credencial.
         */
        citasDelNegocio: (clave: string) => operacionesCitas(conClavePublica(api, clave)),
        /**
         * bookAppointment (clave del negocio + token de cliente + captcha): `201` con la cita y, si hay que pagar, la dirección de
         * pago (`payment_action`, 30 minutos, G39); `409 capacity_unavailable` con alternativas; `422 captcha_required`.
         */
        bookAppointment: (negocioId: string, clave: string, peticion: PeticionCita, captcha: string | null, idempotencia = claveIdempotencia()) =>
            api.pedir<MiCitaConPago>('POST', `${negocio(negocioId)}/appointment-bookings`, {
                cuerpo: peticion,
                idempotencia,
                cabeceras: conClave(clave, captcha === null ? {} : { 'X-Captcha-Token': captcha }),
            }),

        /** listMyAppointments (token de cliente): las de un perfil, próximas o pasadas. */
        listMyAppointments: (perfilId: string, cuando: 'upcoming' | 'past', pagina: Pagina = {}) =>
            api.pedir<ListaMisCitas>('GET', `${misCitas(perfilId)}${consulta({ when: cuando, ...pagina })}`),
        /** getMyAppointment: con lo que puede hacer ahora y el estado del consentimiento. */
        getMyAppointment: (perfilId: string, citaId: string) => api.pedir<MiCita>('GET', `${misCitas(perfilId)}/${encodeURIComponent(citaId)}`),
        /**
         * searchAppointmentSlots con el token de cliente, sin clave pública (G866): los huecos para cambiar una cita desde «Mis
         * citas», con sus servicios, respuestas y profesional y `exclude_appointment_id` (su propio hueco no cuenta).
         */
        searchMyAppointmentSlots: (negocioId: string, busqueda: Esquema<'SlotSearchRequest'>) =>
            api.pedir<ResultadoHuecosCliente>('POST', `${negocio(negocioId)}/appointment-slot-searches`, { cuerpo: busqueda }),
        /** previewMyAppointmentModification: hueco, precio y diferencia; `409 change_window_closed` fuera de plazo (G434). */
        previewMyAppointmentModification: (perfilId: string, citaId: string, cambio: CambioMiCita) =>
            api.pedir<VistaPreviaMiCambio>('POST', `${misCitas(perfilId)}/${encodeURIComponent(citaId)}/modification-previews`, { cuerpo: cambio }),
        /** modifyMyAppointment: `409` sin hueco (con alternativas) o fuera de plazo. */
        modifyMyAppointment: (perfilId: string, citaId: string, cambio: CambioMiCita, idempotencia = claveIdempotencia()) =>
            api.pedir<MiCitaConPago>('POST', `${misCitas(perfilId)}/${encodeURIComponent(citaId)}/modifications`, { cuerpo: cambio, idempotencia }),
        /** getMyAppointmentCancellationPreview: el importe antes de confirmar (D-004). */
        getMyAppointmentCancellationPreview: (perfilId: string, citaId: string) =>
            api.pedir<VistaPreviaMiCancelacion>('GET', `${misCitas(perfilId)}/${encodeURIComponent(citaId)}/cancellation-preview`),
        /** cancelMyAppointment, con el importe visto (`accepted_amount`): si ha cambiado, `409 conflict`. */
        cancelMyAppointment: (perfilId: string, citaId: string, cancelacion: CancelacionMiCita, idempotencia = claveIdempotencia()) =>
            api.pedir<MiCita>('POST', `${misCitas(perfilId)}/${encodeURIComponent(citaId)}/cancellation`, { cuerpo: cancelacion, idempotencia }),

        /** getAppointmentByLink (anónima): la cita del enlace del mensaje, con la vista previa de la cancelación. */
        getAppointmentByLink: (negocioId: string, token: string) => api.pedir<MiCita>('GET', enlaceCita(negocioId, token), { sinSesion: true }),
        /** cancelAppointmentByLink (anónima). */
        cancelAppointmentByLink: (negocioId: string, token: string, cancelacion: CancelacionMiCita, idempotencia = claveIdempotencia()) =>
            api.pedir<MiCita>('POST', `${enlaceCita(negocioId, token)}/cancellation`, { cuerpo: cancelacion, sinSesion: true, idempotencia }),
    };
}
